/**
 * Local person (node) identity for the decentralized demos.
 *
 * The only stable identifier shared across the network is [Identity.peerId], defined as:
 *   peerId = base64url(SPKI(publicKey))
 *
 * This demo persists private key material (platform-specific encoding) for convenience.
 * Do not reuse this persistence format for production secrets.
 */
package com.oasisfeng.invaluable.identity

class Identity(val publicKeySPKI: ByteArray, val privateKeyBytes: ByteArray) {

    /** Stable identity string used on the wire and inside graph entities. */
    val peerId: String = Codec.base64Url(publicKeySPKI)

    fun sign(message: ByteArray): ByteArray = Ed25519.sign(message, privateKeyBytes)

    companion object {

        fun generate(): Identity {
            val keys = Ed25519.generateKeyPair()
            return Identity(keys.publicKeySpki, keys.privateKeyBytes)
        }

        fun serialize(identity: Identity): String =
            """
            |type=ed25519
            |public=${Codec.base64Url(identity.publicKeySPKI)}
            |private=${Codec.base64Url(identity.privateKeyBytes)}
            """.trimMargin() + "\n"

        fun deserialize(text: String): Identity {
            val map = text.lineSequence()
                .mapNotNull { line -> line.trim().takeIf { it.isNotEmpty() && !it.startsWith('#') } }
                .associate { line ->
                    val idx = line.indexOf('=')
                    require(idx > 0) { "Invalid identity line: $line" }
                    line.substring(0, idx).trim() to line.substring(idx + 1).trim()
                }
            val publicBytes = Codec.base64UrlDecode(requireNotNull(map["public"]) { "Missing public key" })
            val privateBytes = Codec.base64UrlDecode(requireNotNull(map["private"]) { "Missing private key" })
            return Identity(publicBytes, privateBytes)
        }
    }
}
